import React from 'react'
import Logo from '../components/shared/Logo'
import { useThemeMode } from '../hooks/useThemeMode'
import { HiOutlineMoon, HiOutlineSun } from 'react-icons/hi'

type LoginLayoutProps = {
    children:React.ReactNode;
}

export default function LoginLayout({children}:LoginLayoutProps) {
    const { mode, isDark, toggleThemeMode } = useThemeMode();

  return (
    <div className="relative flex min-h-screen w-full overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(16,185,129,0.22),_transparent_28%),radial-gradient(circle_at_100%_0%,_rgba(14,165,233,0.18),_transparent_22%),linear-gradient(135deg,_#f9fbfa_0%,_#eef5f1_42%,_#dce9e2_100%)] transition-colors duration-300 dark:bg-[radial-gradient(circle_at_top_left,_rgba(16,185,129,0.2),_transparent_28%),radial-gradient(circle_at_100%_0%,_rgba(56,189,248,0.16),_transparent_22%),linear-gradient(135deg,_#030806_0%,_#08110d_40%,_#0d1914_100%)]">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="absolute -left-24 top-20 h-72 w-72 rounded-full bg-emerald-400/25 blur-3xl dark:bg-emerald-500/18" />
            <div className="absolute right-[-5rem] top-[-3rem] h-80 w-80 rounded-full bg-cyan-300/20 blur-3xl dark:bg-sky-400/16" />
            <div className="absolute bottom-[-6rem] left-1/3 h-80 w-80 rounded-full bg-teal-300/20 blur-3xl dark:bg-teal-300/12" />
            <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.04)_1px,transparent_1px)] bg-[size:120px_120px] opacity-30 dark:opacity-10" />
        </div>

        <div className="relative z-10 flex w-full flex-col px-4 pb-4 pt-4 sm:px-6 sm:pb-6 sm:pt-5 lg:px-7 lg:pb-7">
            <div className="flex w-full items-center justify-between">
                <div className="inline-flex rounded-2xl border border-white/60 bg-white/72 px-4 py-3 shadow-[0_12px_35px_rgba(15,23,42,0.08)] backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/60">
                    <Logo mode={mode} variant='main' className="h-12 max-w-[220px]"/>
                </div>
                <button
                    type="button"
                    onClick={toggleThemeMode}
                    className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-white/60 bg-white/70 text-gray-700 shadow-[0_12px_35px_rgba(15,23,42,0.08)] backdrop-blur-xl transition-colors hover:bg-white dark:border-white/10 dark:bg-slate-900/60 dark:text-gray-100 dark:hover:bg-slate-900"
                    aria-label="Toggle theme"
                >
                    {isDark ? <HiOutlineSun size={20} /> : <HiOutlineMoon size={20} />}
                </button>
            </div>

            <div className="mt-4 grid flex-1 grid-cols-1 gap-4 lg:grid-cols-12 lg:gap-6 xl:gap-8">
                <div className="order-2 flex lg:order-1 lg:col-span-6 xl:col-span-6">
                    <div className="group relative hidden w-full overflow-hidden rounded-[36px] border border-white/55 bg-[#03110d] shadow-[0_40px_100px_rgba(2,6,23,0.22)] backdrop-blur-xl dark:border-white/10 lg:flex">
                        <img src='main_page.jpeg' className="h-full w-full object-cover object-center" alt='App preview' />
                        <div className="absolute inset-0 bg-[linear-gradient(118deg,rgba(3,17,13,0.06)_0%,rgba(4,120,87,0.1)_20%,rgba(2,6,23,0.1)_50%,rgba(2,6,23,0.62)_100%)]" />
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_16%,rgba(16,185,129,0.2),transparent_18%),radial-gradient(circle_at_78%_18%,rgba(34,211,238,0.14),transparent_16%),linear-gradient(to_top,rgba(2,6,23,0.36),transparent_36%)]" />
                        <div className="absolute inset-[18px] rounded-[28px] border border-white/10" />
                        <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/12 to-transparent" />
                        <div className="absolute inset-x-0 bottom-0 h-44 bg-gradient-to-t from-black/58 via-black/18 to-transparent" />
                    </div>
                </div>

                <div className="order-1 flex lg:order-2 lg:col-span-6 xl:col-span-6">
                    <div className="relative flex w-full items-center justify-center px-1 py-6 sm:px-2 lg:px-6">
                        <div className="pointer-events-none absolute -top-8 right-2 h-40 w-40 rounded-full bg-emerald-200/40 blur-3xl dark:bg-emerald-500/12" />
                        <div className="pointer-events-none absolute -bottom-6 left-0 h-36 w-36 rounded-full bg-cyan-200/45 blur-3xl dark:bg-cyan-400/10" />
                        <div className="relative z-10 flex w-full items-center justify-center">
                            {children}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
  )
}
